/** Pure view logic for the GA4 connections page (filters, selection, labels). */

import type { Ga4ConnectionRow, Ga4ConnectionStatus } from '../../types/ga4Connections.js';

export const STATUS_LABEL: Record<Ga4ConnectionStatus, string> = {
  connected: 'Connected',
  suggested: 'Suggested',
  'needs-choice': 'Needs a choice',
  unmatched: 'Unmatched',
  'access-removed': 'Access removed',
};

export interface ConnectionFilters {
  status: Ga4ConnectionStatus | 'all';
  ga4Account: string; // '' = any
  industry: string; // '' = any (the SUGGESTED or connected account's industry)
  search: string;
}

export const NO_FILTERS: ConnectionFilters = { status: 'all', ga4Account: '', industry: '', search: '' };

const industryOf = (r: Ga4ConnectionRow): string | null => r.connectedTo?.industry ?? r.suggestedIndustry;

export function filterRows(rows: readonly Ga4ConnectionRow[], f: ConnectionFilters): Ga4ConnectionRow[] {
  const q = f.search.trim().toLowerCase();
  return rows.filter((r) => {
    if (f.status !== 'all' && r.status !== f.status) return false;
    if (f.ga4Account && (r.ga4AccountName ?? '') !== f.ga4Account) return false;
    if (f.industry && industryOf(r) !== f.industry) return false;
    if (q) {
      const hay = [r.displayName, r.propertyId, r.ga4AccountName ?? '', ...r.websites, r.connectedTo?.accountName ?? '', r.suggestion?.accountName ?? '']
        .join(' ')
        .toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });
}

/** Distinct, sorted option values for the two dropdown filters. */
export function filterOptions(rows: readonly Ga4ConnectionRow[]): { ga4Accounts: string[]; industries: string[] } {
  const a = new Set<string>();
  const i = new Set<string>();
  for (const r of rows) {
    if (r.ga4AccountName) a.add(r.ga4AccountName);
    const ind = industryOf(r);
    if (ind) i.add(ind);
  }
  return { ga4Accounts: [...a].sort(), industries: [...i].sort() };
}

/** Only suggested, non-code rows may be ticked for "Connect selected". */
export const isSelectable = (r: Ga4ConnectionRow): boolean => r.status === 'suggested' && !r.codeConfigured;

/** The property ids "Connect selected" may send: ticked AND still selectable in the visible set. */
export function selectedSuggestedIds(rows: readonly Ga4ConnectionRow[], ticked: ReadonlySet<string>): string[] {
  return rows.filter((r) => isSelectable(r) && ticked.has(r.propertyId)).map((r) => r.propertyId);
}

export function backfillLabel(r: Ga4ConnectionRow): string | null {
  if (!r.backfill) return null;
  const { state, coveredDays, targetDays } = r.backfill;
  if (state === 'complete') return 'History complete';
  if (state === 'waiting') return 'Waiting for the first pull';
  return `Filling history: ${coveredDays} of ${targetDays} days`;
}
